import {
  availableOperators,
  FILTER_OPERATOR_LABELS,
  RELATIVE_PERIOD_LABELS,
  type FieldType,
  type FilterCondition,
  type FilterModel,
  type FilterOperator,
  type RelativePeriod,
  type SelectableColumn,
} from '@rammetec/app-kit/query'

/** Cap shared with `grid.describeFields` — suggestions never exceed this. */
export const ASSISTANT_FIELD_SUGGESTION_CAP = 30

/**
 * Cap for unknown-field error hints (near matches or fallback list of valid fields).
 * Smaller than describeFields so the error stays actionable in one glance.
 */
export const ASSISTANT_FIELD_ERROR_SUGGESTION_CAP = 10

export const FILTER_OPERATOR_ENUM = Object.keys(FILTER_OPERATOR_LABELS) as FilterOperator[]

export const RELATIVE_PERIOD_ENUM = Object.keys(RELATIVE_PERIOD_LABELS) as RelativePeriod[]

const OPERATOR_SET = new Set<string>(FILTER_OPERATOR_ENUM)
const PERIOD_SET = new Set<string>(RELATIVE_PERIOD_ENUM)

export interface FlatFilterCondition {
  field: string
  operator: FilterOperator
  value?: string | number | boolean
  value2?: string | number
  values?: (string | number)[]
  period?: RelativePeriod
}

export interface FlatFilterGroup {
  combinator: 'and' | 'or'
  conditions: FlatFilterCondition[]
}

/** Flat filter shape the model sends to `grid.applyFilter` (request 0289). */
export interface FlatFilterInput {
  combinator: 'and' | 'or'
  conditions: FlatFilterCondition[]
  groups: FlatFilterGroup[]
}

const FLAT_CONDITION_JSON_SCHEMA = {
  type: 'object',
  properties: {
    field: {
      type: 'string',
      description: 'Caminho ou rótulo do campo (liste com grid.describeFields).',
    },
    operator: {
      type: 'string',
      enum: [...FILTER_OPERATOR_ENUM],
    },
    value: {
      description: 'Valor escalar para eq/contains/gt/… (omitir em isEmpty/isNotEmpty).',
    },
    value2: {
      description: 'Segundo valor para o operador between.',
    },
    values: {
      type: 'array',
      items: {},
      description: 'Lista de valores para in/notIn.',
    },
    period: {
      type: 'string',
      enum: [...RELATIVE_PERIOD_ENUM],
      description: 'Período relativo para inPeriod.',
    },
  },
  required: ['field', 'operator'],
  additionalProperties: false,
} as const

/** JSON Schema for `grid.applyFilter` parameters (no $ref — gateway/Gemini safe). */
export const APPLY_FILTER_PARAMETERS_SCHEMA = {
  type: 'object',
  properties: {
    combinator: {
      type: 'string',
      enum: ['and', 'or'],
      description: 'Como combinar as condições e grupos no nível raiz.',
    },
    conditions: {
      type: 'array',
      items: FLAT_CONDITION_JSON_SCHEMA,
      description: 'Condições no nível raiz.',
    },
    groups: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          combinator: { type: 'string', enum: ['and', 'or'] },
          conditions: {
            type: 'array',
            items: FLAT_CONDITION_JSON_SCHEMA,
          },
        },
        required: ['combinator', 'conditions'],
        additionalProperties: false,
      },
      description:
        'Opcional; um nível só (ex.: A e B, ou C). Sem grupos dentro de groups.',
    },
  },
  required: ['combinator'],
  additionalProperties: false,
} as const

/** Exhaustive FieldType walk — TS errors if a new type is added without updating this. */
const APPLY_FILTER_FIELD_TYPES = Object.keys({
  string: true,
  number: true,
  date: true,
  boolean: true,
  enum: true,
  array: true,
  object: true,
} satisfies Record<FieldType, true>) as FieldType[]

/** Derived from `availableOperators` so the prompt cannot drift from validation. */
export function buildApplyFilterOperatorsHint(): string {
  const byType = APPLY_FILTER_FIELD_TYPES.map(
    (type) => `${type} → ${availableOperators(type).join(', ')}`,
  ).join('; ')
  const fk = availableOperators('string', { fk: true }).join(', ')
  return (
    `Operadores: ${byType}; ` +
    `fk (coluna de relação, › em describeFields) → ${fk}. ` +
    'enum/fk: in com values (não eq/value).'
  )
}

export const APPLY_FILTER_TOOL_DESCRIPTION =
  'Aplica filtro ao grid dinâmico. Envie o esquema plano (não o FilterModel interno com kind/id/label). ' +
  'Exemplo: {"combinator":"and","conditions":[{"field":"reference","operator":"contains","value":"18"}]}. ' +
  `${buildApplyFilterOperatorsHint()} ` +
  'Caminhos/rótulos válidos: use grid.describeFields. Grupos aninhados além de um nível não são suportados.'

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

export function matchesFieldBusca(col: SelectableColumn, busca: string): boolean {
  const q = busca.trim().toLowerCase()
  if (!q) return true
  return (
    col.path.toLowerCase().includes(q) ||
    col.label.toLowerCase().includes(q) ||
    col.pathLabel.toLowerCase().includes(q)
  )
}

/**
 * Stricter match for typo suggestions in tool errors.
 * Substring-in-the-middle (`"id"` inside `quantityDecimals`) is rejected — that led the
 * model to retry with junk. Require prefix of path/label/pathLabel, or a whole token
 * (non-alphanumeric split or camelCase part).
 */
export function matchesFieldSuggestion(col: SelectableColumn, busca: string): boolean {
  const q = busca.trim().toLowerCase()
  if (!q) return false

  const matchesRaw = (raw: string): boolean => {
    const s = raw.toLowerCase()
    if (s.startsWith(q)) return true
    if (s.split(/[^a-z0-9]+/).includes(q)) return true
    const camelParts = raw
      .split(/(?=[A-Z])/)
      .map((p) => p.toLowerCase())
      .filter(Boolean)
    return camelParts.includes(q)
  }

  return matchesRaw(col.path) || matchesRaw(col.label) || matchesRaw(col.pathLabel)
}

/** Typo suggestions for unknown-field errors — prefix / whole-token only, capped. */
export function suggestColumns(
  columns: readonly SelectableColumn[],
  busca: string,
  cap = ASSISTANT_FIELD_ERROR_SUGGESTION_CAP,
): SelectableColumn[] {
  return columns.filter((c) => matchesFieldSuggestion(c, busca)).slice(0, cap)
}

export function formatSuggestedFields(columns: readonly SelectableColumn[]): string {
  if (columns.length === 0) {
    return 'nenhum campo filtrável disponível'
  }
  return columns
    .map((c) => `${c.path} (${c.pathLabel || c.label}, ${c.type})`)
    .join('; ')
}

/**
 * Error copy when the model invents a field name.
 * Near matches first; if none, list the first own/valid columns so the model can retry
 * without another describeFields round (especially after a narrow busca).
 */
export function formatUnknownFieldError(
  field: string,
  columns: readonly SelectableColumn[],
  cap = ASSISTANT_FIELD_ERROR_SUGGESTION_CAP,
): string {
  const near = suggestColumns(columns, field, cap)
  if (near.length > 0) {
    return `não entendi: campo "${field}". Campos próximos: ${formatSuggestedFields(near)}`
  }
  const fallback = columns.slice(0, cap)
  if (fallback.length === 0) {
    return `não entendi: campo "${field}". Nenhum campo filtrável disponível.`
  }
  return `não entendi: campo "${field}". Campos válidos: ${formatSuggestedFields(fallback)}`
}

export function resolveFilterableColumn(
  field: string,
  columns: readonly SelectableColumn[],
): SelectableColumn | null {
  const trimmed = field.trim()
  if (!trimmed) return null

  const exact = columns.find((c) => c.path === trimmed)
  if (exact) return exact

  const lower = trimmed.toLowerCase()
  const byPath = columns.find((c) => c.path.toLowerCase() === lower)
  if (byPath) return byPath

  const byLabel = columns.filter(
    (c) => c.label.toLowerCase() === lower || c.pathLabel.toLowerCase() === lower,
  )
  if (byLabel.length === 1) return byLabel[0]!
  return null
}

function parseScalar(value: unknown): string | number | boolean | undefined {
  if (value === undefined || value === null) return undefined
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value
  }
  return undefined
}

function parseValueList(value: unknown): (string | number)[] | undefined {
  if (!Array.isArray(value)) return undefined
  const out: (string | number)[] = []
  for (const item of value) {
    if (typeof item === 'string' || typeof item === 'number') out.push(item)
    else return undefined
  }
  return out
}

function parseFlatCondition(
  raw: unknown,
  where: string,
): { ok: true; condition: FlatFilterCondition } | { ok: false; error: string } {
  if (!isRecord(raw)) {
    return { ok: false, error: `não entendi: ${where} deve ser um objeto { field, operator, … }` }
  }
  if (typeof raw.field !== 'string' || !raw.field.trim()) {
    return { ok: false, error: `não entendi: ${where} exige "field" (texto não vazio)` }
  }
  if (typeof raw.operator !== 'string' || !OPERATOR_SET.has(raw.operator)) {
    return {
      ok: false,
      error: `não entendi: ${where} operador inválido "${String(raw.operator)}". Aceitos: ${FILTER_OPERATOR_ENUM.join(', ')}`,
    }
  }
  const operator = raw.operator as FilterOperator
  const condition: FlatFilterCondition = {
    field: raw.field.trim(),
    operator,
  }

  if (raw.value !== undefined) {
    const v = parseScalar(raw.value)
    if (v === undefined) {
      return {
        ok: false,
        error: `não entendi: ${where} "value" deve ser texto, número ou booleano (campo "${condition.field}")`,
      }
    }
    condition.value = v
  }
  if (raw.value2 !== undefined) {
    const v = parseScalar(raw.value2)
    if (v === undefined || typeof v === 'boolean') {
      return {
        ok: false,
        error: `não entendi: ${where} "value2" deve ser texto ou número (campo "${condition.field}")`,
      }
    }
    condition.value2 = v
  }
  if (raw.values !== undefined) {
    const list = parseValueList(raw.values)
    if (!list) {
      return {
        ok: false,
        error: `não entendi: ${where} "values" deve ser lista de texto/número (campo "${condition.field}")`,
      }
    }
    condition.values = list
  }
  if (raw.period !== undefined) {
    if (typeof raw.period !== 'string' || !PERIOD_SET.has(raw.period)) {
      return {
        ok: false,
        error: `não entendi: ${where} "period" inválido "${String(raw.period)}". Aceitos: ${RELATIVE_PERIOD_ENUM.join(', ')}`,
      }
    }
    condition.period = raw.period as RelativePeriod
  }

  return { ok: true, condition }
}

function parseConditionList(
  raw: unknown,
  where: string,
): { ok: true; conditions: FlatFilterCondition[] } | { ok: false; error: string } {
  if (raw === undefined) return { ok: true, conditions: [] }
  if (!Array.isArray(raw)) {
    return { ok: false, error: `não entendi: ${where} deve ser uma lista` }
  }
  const conditions: FlatFilterCondition[] = []
  for (let i = 0; i < raw.length; i++) {
    const parsed = parseFlatCondition(raw[i], `${where}[${i}]`)
    if (!parsed.ok) return parsed
    conditions.push(parsed.condition)
  }
  return { ok: true, conditions }
}

/**
 * Parses the flat applyFilter payload. Rejects the old FilterModel shape with a teaching error.
 */
export function parseFlatFilterInput(
  raw: unknown,
): { ok: true; input: FlatFilterInput } | { ok: false; error: string } {
  if (!isRecord(raw)) {
    return {
      ok: false,
      error:
        'não entendi: envie um objeto { combinator, conditions?, groups? }. Exemplo: {"combinator":"and","conditions":[{"field":"reference","operator":"contains","value":"18"}]}',
    }
  }

  if (raw.filter !== undefined) {
    return {
      ok: false,
      error:
        'não entendi: não use a chave "filter"; envie combinator/conditions no objeto raiz. Exemplo: {"combinator":"and","conditions":[{"field":"reference","operator":"contains","value":"18"}]}',
    }
  }

  if (raw.kind !== undefined || raw.children !== undefined || raw.path !== undefined) {
    return {
      ok: false,
      error:
        'não entendi: não envie FilterModel interno (kind/id/path/label/children). Use o esquema plano { combinator, conditions, groups? }. Exemplo: {"combinator":"and","conditions":[{"field":"reference","operator":"contains","value":"18"}]}',
    }
  }

  if (raw.combinator !== 'and' && raw.combinator !== 'or') {
    return {
      ok: false,
      error: 'não entendi: "combinator" deve ser "and" ou "or"',
    }
  }

  const rootConditions = parseConditionList(raw.conditions, 'conditions')
  if (!rootConditions.ok) return rootConditions

  let groups: FlatFilterGroup[] = []
  if (raw.groups !== undefined) {
    if (!Array.isArray(raw.groups)) {
      return { ok: false, error: 'não entendi: "groups" deve ser uma lista' }
    }
    groups = []
    for (let i = 0; i < raw.groups.length; i++) {
      const g = raw.groups[i]
      if (!isRecord(g)) {
        return { ok: false, error: `não entendi: groups[${i}] deve ser um objeto` }
      }
      if (g.groups !== undefined || g.children !== undefined) {
        return {
          ok: false,
          error:
            'não suportei grupos aninhados além de um nível; simplifique (só conditions dentro de groups).',
        }
      }
      if (g.combinator !== 'and' && g.combinator !== 'or') {
        return {
          ok: false,
          error: `não entendi: groups[${i}].combinator deve ser "and" ou "or"`,
        }
      }
      const nested = parseConditionList(g.conditions, `groups[${i}].conditions`)
      if (!nested.ok) return nested
      groups.push({ combinator: g.combinator, conditions: nested.conditions })
    }
  }

  return {
    ok: true,
    input: {
      combinator: raw.combinator,
      conditions: rootConditions.conditions,
      groups,
    },
  }
}

function missingValueError(c: FlatFilterCondition): string | null {
  switch (c.operator) {
    case 'isEmpty':
    case 'isNotEmpty':
      return null
    case 'in':
    case 'notIn':
      if (!c.values?.length) {
        return `falta valor: operador "${c.operator}" exige "values" (lista não vazia) no campo "${c.field}"`
      }
      return null
    case 'between':
      if (
        c.value === undefined ||
        c.value === '' ||
        c.value2 === undefined ||
        c.value2 === ''
      ) {
        return `falta valor: operador "between" exige "value" e "value2" no campo "${c.field}"`
      }
      return null
    case 'inPeriod':
      if (!c.period) {
        return `falta valor: operador "inPeriod" exige "period" (${RELATIVE_PERIOD_ENUM.join('|')}) no campo "${c.field}"`
      }
      return null
    default:
      if (c.value === undefined || c.value === '') {
        return `falta valor: operador "${c.operator}" exige "value" no campo "${c.field}"`
      }
      return null
  }
}

function toFilterCondition(
  flat: FlatFilterCondition,
  col: SelectableColumn,
): FilterCondition {
  const cond: FilterCondition = {
    kind: 'condition',
    id: crypto.randomUUID(),
    path: col.path,
    label: col.pathLabel || col.label,
    type: col.type,
    origin: col.origin,
    operator: flat.operator,
  }
  if (col.fk) cond.fk = col.fk

  switch (flat.operator) {
    case 'isEmpty':
    case 'isNotEmpty':
      break
    case 'in':
    case 'notIn':
      cond.values = flat.values
      break
    case 'between':
      cond.value = flat.value
      cond.value2 = flat.value2
      break
    case 'inPeriod':
      cond.period = flat.period
      break
    default:
      cond.value = flat.value
      break
  }
  return cond
}

function buildCondition(
  flat: FlatFilterCondition,
  columns: readonly SelectableColumn[],
): { ok: true; condition: FilterCondition } | { ok: false; error: string } {
  const col = resolveFilterableColumn(flat.field, columns)
  if (!col) {
    return { ok: false, error: formatUnknownFieldError(flat.field, columns) }
  }

  const ops = availableOperators(col.type, { fk: col.fk })
  if (!ops.includes(flat.operator)) {
    return {
      ok: false,
      error: `operador "${flat.operator}" inválido para o tipo ${col.type} do campo "${col.path}". Aceitos: ${ops.join(', ')}`,
    }
  }

  const missing = missingValueError(flat)
  if (missing) return { ok: false, error: missing }

  return { ok: true, condition: toFilterCondition(flat, col) }
}

/**
 * Resolves fields/operators against filterable columns and builds a FilterModel v2.
 */
export function buildFilterModelFromFlat(
  input: FlatFilterInput,
  columns: readonly SelectableColumn[],
): { ok: true; model: FilterModel } | { ok: false; error: string } {
  const rootChildren: FilterCondition[] = []
  for (const flat of input.conditions) {
    const built = buildCondition(flat, columns)
    if (!built.ok) return built
    rootChildren.push(built.condition)
  }

  const groupNodes: FilterModel[] = []
  for (const group of input.groups) {
    const children: FilterCondition[] = []
    for (const flat of group.conditions) {
      const built = buildCondition(flat, columns)
      if (!built.ok) return built
      children.push(built.condition)
    }
    groupNodes.push({
      kind: 'group',
      id: crypto.randomUUID(),
      combinator: group.combinator,
      children,
    })
  }

  return {
    ok: true,
    model: {
      kind: 'group',
      id: crypto.randomUUID(),
      combinator: input.combinator,
      children: [...rootChildren, ...groupNodes],
    },
  }
}
